// @ts-check
import _, { isString, toLower, startCase } from 'lodash';
import {
  EMAIL_REGEXP,
  OSP_PRODUCT_SHARE_EXPIRY_DATE,
  DAYS_IN_MILLISECONDS,
  OSP_PRODUCT_SHARE_EXPIRY,
  NO_IMAGE_AVAILABLE,
  IMAGE_HEAD_PROTOCOL,
  NON_DIGIT_REGEX,
  DEFAULT_USER_IMAGE_MAP,
  DIGITAL_STYLIST_GROUPS,
  MIN_NAME_LENGTH_CC_FORM,
  NAME_REGEX,
  STYLYZE_LOOKS_LINKS,
  STYLYZE_EDIT_CATEGORIES,
  EDITS_CATEGORY,
} from 'constants/index';

const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

/*
  Group Clients by first letter in firstName
 */
const groupClients = (clients) =>
  _.chain(clients)
    .groupBy((client) => client.firstName[0].toUpperCase())
    .map((contacts, letter) => ({
      letter,
      contacts: _.sortBy(contacts, (user) => user.firstName.toLowerCase(), ['desc']),
      id: letter + Math.floor(Math.random() * 1000),
    }))
    .sortBy((user) => user.letter, ['desc'])
    .value();

/*
  Search clients by firstName and lastName
 */
const searchClient = (client, search) => {
  const { firstName, lastName, email } = client;
  const name = `${firstName} ${lastName}`.trim().toLocaleLowerCase();
  const srch = search.trim().toLowerCase();

  return name.indexOf(srch) >= 0 || email.toLowerCase().indexOf(srch) >= 0;
};

/*
  Function for validating name field in Client Create Form
 */
const validateName = (name) => name.length >= MIN_NAME_LENGTH_CC_FORM && NAME_REGEX.test(name);

/**
 * Validate email.
 * @param {string} email
 * @return {boolean}
 */
// eslint-disable-next-line no-useless-escape
const validateEmail = (email) => /^\w{2,}([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,5})+$/.test(email);

/*
  Hide full email.
  Only displays first 2 characters and domain
 */
const formatEmail = (email) => (validateEmail(email) ? `${email.substring(0, 2)}****@${email.split('@')[1]}` : '');

/*
  Updated function for validating email field in Client Create Form
 */
const validateEmailV2 = (email) => {
  const EMAIL_REGEX =
    // eslint-disable-next-line no-useless-escape
    /^[a-zA-Z0-9.!#$%&'*+\/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  return EMAIL_REGEX.test(email);
};

/*
  Format phone
 */
const formatPhone = (phone) => {
  if (!phone) return '';

  const str = phone.toString().replace(NON_DIGIT_REGEX, '');
  const end = str.length - 4;
  return (
    end > 0 &&
    `${str
      .substring(0, end)
      .match(/.{1,3}/g)
      .join('-')}-${str.substring(end)}`
  );
};

/*
    Format phone numbers for voice and SMS calls through Twilio

 *   Use phoneNumberFormatter(phoneNum, returnType) to format a phone number into a desired format
 *   params: the phone number you want to convert and the styling you want returned
 *   available return types:
 *     dashes = 123-456-7890
 *     outBoundVoice = 1234567890
 *     parens = (123) 456-7890
 *     plus1 = +11234567890
 *
 *  TODO: Need to update this to handle country codes and extensions eventually. Most likely this will require additional params.
 **/
const phoneNumberFormatter = (phoneNum = '', returnType) => {
  let formattedPhoneNumber = '';
  let numberToParse = phoneNum.trim();

  const regex = /[^0-9]+/g;
  const firstTwoChars = numberToParse.substring(0, 2);

  // first check if number sent starts with +1. if so remove it.
  if (firstTwoChars === '+1') {
    numberToParse = phoneNum.trim().substring(2, 22);
  }
  const phoneDigitsOnly = numberToParse.replace(regex, '');

  // check that we have at least 10 digits left to make sure it might be a valid number after everything has been stripped
  if (phoneDigitsOnly.length < 10) {
    return '';
  }

  const phoneAreaCode = phoneDigitsOnly.substring(0, 3);
  const phonePrefix = phoneDigitsOnly.substring(3, 6);
  const phoneSubscriberNumber = phoneDigitsOnly.substring(6, 10);

  switch (returnType) {
    case 'dashes':
      formattedPhoneNumber = `${phoneAreaCode}-${phonePrefix}-${phoneSubscriberNumber}`;
      break;
    case 'outBoundVoice':
      formattedPhoneNumber = `${phoneDigitsOnly}`;
      break;
    case 'parens':
      formattedPhoneNumber = `(${phoneAreaCode}) ${phonePrefix}-${phoneSubscriberNumber}`;
      break;
    case 'plus1':
      formattedPhoneNumber = `+1${phoneDigitsOnly}`;
      break;
    default:
      formattedPhoneNumber = phoneNum;
      break;
  }

  return formattedPhoneNumber;
};

/**
 * Formats keypad input to a US phone number format without extension.
 * @param {string} phoneNum Contains up to 10 digits only
 * @returns {string}
 */
const formatKeypadPhone = (phoneNum) => {
  if (phoneNum.length < 3) {
    return phoneNum;
  }

  let result = `(${phoneNum.substring(0, 3)}) ${phoneNum.substring(3, 6)}`;

  if (phoneNum.length > 6) {
    result += `-${phoneNum.substring(6, 10)}`;
  }

  return result;
};

/**
 * Get Total price of the added products.
 * @param {Array} products
 * @return {string} - string.toFixed(2)
 */
const getProductsTotalPrice = (products) => {
  if (!Array.isArray(products)) {
    return '0.00';
  }

  return products
    .reduce((acc, { price = {}, quantity = 1 }) => {
      const itemPrice =
        price.adjustedPromoPrice ||
        price.promoPrice ||
        price.retailPrice ||
        price.itemPriceEach ||
        price.adjustedPrice ||
        price.currentPrice ||
        0;
      const singleProductPrice = itemPrice * quantity;
      return acc + singleProductPrice;
    }, 0)
    .toFixed(2);
};

/**
 * Get default payment
 * @param {Array} payments
 * @returns {Object|undefined} - payment object
 */
const getDefaultPayment = (payments = []) => payments.find((p) => p.defaultCard) || payments[0];

/**
 * Get selected payment
 * @param {Array} payments
 * @returns {Object|undefined} - payment object
 */
const getSelectedPayment = (payments = []) => payments.find((p) => p.selected) || getDefaultPayment(payments);

/**
 * Return unified productData mapped from different data sources
 * @param productData
 * @returns {*&{itemQty: *, brandName: *, pimSkuId: *, adjustedPrice: *, imageUrl: *, currentPrice: *}}
 */
const mapProductData = (productData) => {
  return {
    brandName: productData.brandName || productData.brand,
    pimSkuId: productData.pimSkuId || productData.itemskuId,
    imageUrl: productData.imageUrl || productData.thumbnailUrl,
    currentPrice: productData.currentPrice || productData.price?.currentPrice,
    adjustedPrice: productData.adjustedPrice || productData.price?.adjustedPrice,
    itemQty: productData.itemQty || productData.quantity,
    ...productData,
  };
};

/**
 * Get default address
 * @param {Array} addresses
 * @returns {Object|undefined} - object
 */
const getDefaultShipping = (addresses = []) => {
  return addresses.find((p) => p.defaultAddress || p.default) || addresses[0];
};

/**
 * Get payment by id
 * @param {Array} payments
 * @param {String} id
 * @returns {Object|undefined} - payment object
 */
const getPaymentById = (payments = [], id, keyName) => {
  if (keyName) {
    return payments.find((p) => p[keyName] === id);
  }
  return payments.find((p) => (p?.paymentId ? p.paymentId === id : p?.keyIdentifier === id));
};

/**
 * Get last 4 digits from card number
 * @param {Object} payment
 * @returns {String}
 */
const getLast4Digits = (payment = {}) => payment.maskedNumber?.slice(-4);

/*
  Get default value.
  If array has an object with `default: true`, display it as a default.
  Otherwise display first item in the array as default.
 */
const getDefaultValue = (values = []) => {
  const defaultValue = values.find((item) => item.default || item.defaultCard);
  const value = defaultValue || values[0];
  return value || {};
};

/*
  Get selected value || first item in the array as default
 */
const getSelectedValue = (values = []) => {
  const selected = values.find((item) => item.selected);
  return selected || getDefaultValue(values) || {};
};

const formatImageUrl = (url) => url.replace(/^(https:\/\/|http:\/\/)?/, `${IMAGE_HEAD_PROTOCOL}//`);

/*
  Format base url
  Remove extra '/' if it is present in url
 */
const formatBaseUrl = (url) => {
  const lastChar = url.slice(-1);
  return lastChar === '/' ? url : `${url}/`;
};

/**
 * Format name to
 * @param {string} str // 'John Doe'
 * @returns {string} JD
 */
const getNameAbbreviation = (str) => {
  const matches = str.match(/\b(\w)/g);
  return matches ? matches.join('') : '';
};

const phoneToNumber = (phone = '') => {
  if (typeof phone === 'number') {
    return phone;
  }
  return Number(phone.replace(NON_DIGIT_REGEX, ''));
};

/**
 * Get product by itemskuId from cart collection
 * @param {Array} cart
 * @param {string} itemskuId
 * @return {object} - product
 */
const findProductInCartById = (cart, itemskuId) => _.find(cart, (obj) => obj.itemskuId === itemskuId);

/**
 * Get object by fieldName & fieldValue from array
 * @param {Array} arr
 * @param {string} fieldName
 * @param {string} fieldValue
 * @return {object}
 */
const findObjByFieldValue = (arr, fieldName, fieldValue) => _.find(arr, (obj) => obj[fieldName] === fieldValue);

/**
 * Validate url
 * @param {string} url
 */
const isUrlValid = (url) => {
  // eslint-disable-next-line no-useless-escape
  const regex =
    /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9/_]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/gm;
  return regex.test(url);
};

/**
 * Validate input value
 * @param {string} value
 */
const isNumber = (value) => (Number.isNaN(parseFloat(value)) ? '' : value.toString().replace(NON_DIGIT_REGEX, ''));

const removeNumbersFromString = (value) => value.replace(/[0-9]+/g, '');

const getRetailPrice = (price = '', promo = '') => (promo ? parseFloat(price) + parseFloat(promo) : price);

/**
 * Replaces multimple new-lines with single
 * @param {string} text
 */
const replaceNewLines = (text) => {
  // match multiple new-lines in the row
  const newLinesRegExp = /[\n]+/g;
  return _.replace(text, newLinesRegExp, '\n');
};

/**
 * Joins array items(strings) with new-lines
 * @param {array} array
 * @returns {string}
 */
const joinArrayOfStringsWithNewLines = (array) => {
  return _.trimEnd(array.join('\n'));
};

/**
 * Checks if the passed string is valid SKU
 * @param {string} str
 * @returns {boolean}
 */
const isValidSKU = (str = '') => {
  // starts with 4, only numbers and only 12 symbols
  const validSKU = /^4[0-9]\d{10}$/;
  return validSKU.test(str);
};

const isValidEmail = (email) => {
  return EMAIL_REGEXP.test(email);
};

/**
 * Get notifications counter
 * @param {array} notifications
 * @returns {number}
 */
const getNotificationsCounter = (notifications) => {
  return notifications?.reduce((acc, obj) => acc + obj.notificationCount, 0);
};

/**
 * Create UUID
 * @returns {string}
 */
const uuidv4 = () =>
  // @ts-ignore
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    // eslint-disable-next-line no-bitwise, no-undef
    (c ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))).toString(16),
  );

/* converts number to a string with comma for every 3 digits
  e.g.  1233 -> '1,233', 1000000 -> '1,000,000' 13 -> '13'
*/
const formatCommaSeparatedNumber = (value) => {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const capitalizeString = (str) => {
  if (!str) return str;

  return str
    .split(' ')
    .map((word) => (word.length ? word[0].toUpperCase() + word.slice(1).toLowerCase() : ''))
    .join(' ');
};

/**
 * Generate unique static IDs for given prefix
 * @param {string} prefix
 * @returns {Generator<string>}
 */
function* generatePrefixedStaticIDs(prefix) {
  let count = 0;

  while (true) {
    count += 1;
    const ID = `${prefix}_${count.toString(16)}`;
    yield ID;
  }
}

/*
  Get product price
 */
const getProductPrice = (price = {}) => `${price.currencyCode || ''}${price.retailPrice || ''}`;

const stringToArray = (value = '', separator) => value.split(separator || ',').filter(Boolean);
/**
 * get initials from full name
 * @param {string} name
 */
const getInitials = (name = '') =>
  name &&
  name
    .split(' ')
    .map((n) => n[0])
    .join('');

/**
 * Converts upper case to lower case and replace the first letter with the upper case (OTHER => Other)
 * @param {string} str
 * @returns {string}
 */
const capitalize = (str = '') => str.toLowerCase().replace(/^\w/, (c) => c.toUpperCase());

/**
 * Capitalizes every word in a string
 * @param {string} str
 * @returns {string}
 */
const toTitleCase = (str = '') =>
  toLower(str)
    .split(' ')
    .map((word) => {
      // skip word if it consist not only of letters
      if (!/^[a-zA-Z]+(-\w+)*$/.test(word)) {
        return word;
      }
      return startCase(word);
    })
    .join(' ');

/**
 * Returns contacts with divider
 * @param {array | string | undefined} phoneNumbers
 * @param {array | string | undefined} emails
 * @param {string} dividerSign
 * @returns {string}
 */
const mapClientContacts = (phoneNumbers, emails, dividerSign = '|') => {
  let phone;
  let email;

  if (phoneNumbers && phoneNumbers.length) {
    if (Array.isArray(phoneNumbers)) {
      phone = phoneNumbers[0].number;
    }

    if (typeof phoneNumbers === 'string') {
      phone = phoneNumbers;
    }
  } else {
    phone = '';
  }

  if (emails && emails.length) {
    if (Array.isArray(emails)) {
      // eslint-disable-next-line prefer-destructuring
      email = emails[0];
    }

    if (typeof emails === 'string') {
      email = emails;
    }
  } else {
    email = '';
  }

  const divider = phone && email ? ` ${dividerSign} ` : '';

  return `${phone}${divider}${email}`;
};

/**
 todays date + one day , used to pass in to associate attribution link
 */

const getAttributionQuery = (associatePin, userBrand) => {
  const brandAwareChannel = userBrand.toLowerCase();
  const linkExpDate = Date.now() + OSP_PRODUCT_SHARE_EXPIRY_DATE * DAYS_IN_MILLISECONDS;
  return `?channel=${brandAwareChannel}connect&associatePin=${associatePin}&${OSP_PRODUCT_SHARE_EXPIRY}=${linkExpDate}`;
};

/**
 * getCompressedImageUrl is used to achive image compression
 * See details:
 * https://cloudinary.com/blog/how_to_automate_compression_of_video_files_with_one_line_of_code
 *
 * q_40 is static since mobile providers have their own compression
 * when it comes to big size images
 */
const getCompressedImageUrl = (url, urlPartToReplace) => url.replace(urlPartToReplace, `${urlPartToReplace}q_40/`);
/**
 * Retrieve code version from the `<meta name="version" />`
 * @returns {String|null}
 */
function getAppVersion() {
  const metaTag = document.querySelector('meta[name="version"]');
  let version = null;

  if (metaTag) {
    version = metaTag.getAttribute('content');
  }

  return version;
}

/**
 * Converts camel case string to space-delimited string
 * @param {string | undefined} camelCasedString
 * @returns {String}
 */
const camelCaseToSpaceDelimited = (camelCasedString = '') =>
  camelCasedString
    .replace(/(^[a-z]+)|[0-9]+|[A-Z][a-z]+|[A-Z]+(?=[A-Z][a-z]|[0-9])/g, (match, first) => {
      // eslint-disable-next-line no-param-reassign
      if (first) match = match[0].toUpperCase() + match.substring(1);
      return `${match} `;
    })
    .trim();

/**
 * Function to retrieve object from URL search params
 * @param {String} [searchParam] "?text=search"
 * @param {Array<string>} [arrayLikeFields] defines those fields value of which should be parsed as an Array
 * @returns {Object}
 */
const getQueryParams = (searchParam, arrayLikeFields = []) => {
  const queryParams = {};
  const newParams = new URLSearchParams(searchParam);
  const booleanValues = ['true', 'false'];

  newParams.forEach((value, key) => {
    let parsedValue = decodeURIComponent(value);

    if (arrayLikeFields.includes(key) && isString(parsedValue)) {
      // parse array of values from target parameter
      // @ts-ignore
      parsedValue = _.compact(parsedValue.split(','));
    } else if (booleanValues.includes(parsedValue)) {
      // parse boolean value
      // @ts-ignore
      parsedValue = parsedValue === 'true';
    }

    queryParams[key] = parsedValue;
  });

  return queryParams;
};

/**
 * Creates query-string from queryParams object
 * @param {Object} queryParams
 * @returns {String}
 */
const createQueryString = (queryParams) => {
  const querySubStringsArray = Object.entries(queryParams).map(
    ([key, value]) => value && `${key}=${encodeURIComponent(value)}`,
  );
  return querySubStringsArray.filter(Boolean).join('&');
};

/**
 * Checks that the provided string is an HTML
 * @param {String} string
 * @returns {Boolean}
 */
const isHTML = (string) => {
  const regEx =
    /<(br|basefont|hr|input|source|frame|param|area|meta|!--|col|link|option|base|img|wbr|!DOCTYPE).*?>|<(a|abbr|acronym|address|applet|article|aside|audio|b|bdi|bdo|big|blockquote|body|button|canvas|caption|center|cite|code|colgroup|command|datalist|dd|del|details|dfn|dialog|dir|div|dl|dt|em|embed|fieldset|figcaption|figure|font|footer|form|frameset|head|header|hgroup|h1|h2|h3|h4|h5|h6|html|i|iframe|ins|kbd|keygen|label|legend|li|map|mark|menu|meter|nav|noframes|noscript|object|ol|optgroup|output|p|pre|progress|q|rp|rt|ruby|s|samp|script|section|select|small|span|strike|strong|style|sub|summary|sup|table|tbody|td|textarea|tfoot|th|thead|time|title|tr|track|tt|u|ul|var|video).*?<\/\2>/i;
  return regEx.test(string);
};

const getFullUserName = (data) => (data ? `${data.firstName} ${data.lastName}` : '');

const getLocaleSpecificNumericString = (number) => {
  const parsedNumber = parseFloat(number);
  return _.isFinite(parsedNumber) ? parsedNumber.toLocaleString() : '';
};

/**
 * @param {number} price
 * @param {boolean} shouldRound
 * @return {string}
 */
const getPriceString = (price, shouldRound = false) => {
  const resultPrice = shouldRound ? Math.round(price) : price;
  if (!resultPrice) {
    return '';
  }
  return `$${getLocaleSpecificNumericString(resultPrice)}`;
};

const getConciergeBagTotalDetails = (conciergeCartData, products) => {
  const productsCount = products?.length;
  const formattedPrice = getLocaleSpecificNumericString(conciergeCartData?.totalPrice);

  return {
    totalPrice: `$${formattedPrice}`,
    totalItems: productsCount !== 1 ? `${productsCount} Items` : `${productsCount} Item`,
  };
};

/**
 * Replaces image source with fallback one
 * @param {Event} event
 * @param {String} [userBrand]
 * @param {String} [fallback]
 */
const onMediaFallback = (event, userBrand = 'NM', fallback) => {
  const noImageAvailable = fallback || NO_IMAGE_AVAILABLE[userBrand];
  // @ts-ignore
  // eslint-disable-next-line no-param-reassign
  event.target.src = noImageAvailable;
};

/**
 * Get next page cursor
 * @param {Array} items
 * @returns {Number}
 */
const getNextPageCursor = (items, startingIndex = 1) => {
  return _.isArray(items) ? items.length + startingIndex : startingIndex;
};

/**
 * Function to return length of provided string after trimming whitespace from both sides
 * @param {String} [string] ex.1:" blue "  ex.2:"  r"  ex.3:" r o se "
 * @returns {number} ex.1: 4,  ex.2: 1, ex.3: 6
 */
const trimWhitespaceLength = (string = '') => string.trim().length;

/**
 * This function is used to merge two sorted arrays by utilizing `comparator`
 * which allow to compare each value for achieving sorted array.
 * **Default comparator:**
 * `(a, b) => a[0] < b[0]`
 *
 * **Note:** In order to work properly both sub-arrays must be sorted first!;
 *
 * @param {Array} a sub-array of half A
 * @param {Array} b sub-array of half B
 * @param {Function} comparator will compare value of each item in both sub-arrays
 * @returns {Array} merged array with sorting order
 * @example
 *
 * function comparator(a, b) {
 *   return a[0].value < b[0].value;
 * }
 *
 * const arr1 = [{ value: 1 }, { value: 5 }];
 * const arr2 = [{ value: 10 }, { value: 100}];
 *
 * _mergeArrays(arr1, arr2, comparator);
 * // => [{ value: 1 }, { value: 5 }, { value: 10 }, { value: 100 }];
 */
const _mergeArrays = (a, b, comparator = (a, b) => a[0] < b[0]) => {
  const subArrA = [...a];
  const subArrB = [...b];
  const result = [];

  while (subArrA.length && subArrB.length) {
    result.push(comparator(subArrA, subArrB) ? subArrA.shift() : subArrB.shift());
  }

  // concatenate the leftover values to the end of array
  return [...result, ...subArrA, ...subArrB];
};

const trimWebProductId = (webProductId) =>
  webProductId?.includes('-') ? webProductId.substring(0, webProductId.lastIndexOf('-')) : webProductId;

const callDuration = (callTimer) => {
  // timer in 00:00:00 format
  const secondsInMinute = 60;
  const secondsInHour = 3600;
  if (callTimer) {
    const hours = Math.floor(callTimer / secondsInHour)
      .toString()
      .padStart(2, '0');
    const minutes = Math.floor((callTimer % secondsInHour) / secondsInMinute)
      .toString()
      .padStart(2, '0');
    const seconds = Math.floor(callTimer % secondsInMinute)
      .toString()
      .padStart(2, '0');
    return `${hours} : ${minutes} : ${seconds}`;
  }
};

const getChatMessageAttributes = ({
  associatePin,
  UCAProfileId,
  customerName,
  imageSource,
  assets,
  attachmentsArr,
  tags,
  isComissionAttributionEnabled,
}) => {
  /**
   * @type object
   */

  const attributes = {
    associatePin,
    profileId: UCAProfileId,
    customerName,
    from: associatePin,
    to: UCAProfileId,
    attachments: [],
    tags,
  };
  //* The max limit for attributes is 4Kib
  //* https://www.twilio.com/docs/chat/chat-limits#resource-property-limits
  if (imageSource?.length) {
    imageSource.forEach((img) => {
      //* All attached files -> actualImageUrl
      //* All shared products and looks -> imageUrl
      const url = img?.item?.actualImageUrl || img?.item?.imageUrl;
      const {
        item: {
          resourceType,
          fileSize,
          selectedLookId,
          inStoreOnly,
          pimSkuId,
          productId,
          skuId,
          isSharedProduct,
          productSku,
          nmgPimId,
          productInfo,
        },
      } = img;
      attributes.attachments.push({
        url,
        ...((!productId || !skuId) && { id: img.id }),
        resourceType,
        bytes: fileSize,
        lookId: selectedLookId,
        inStoreOnly: !!inStoreOnly,
        productId,
        skuId: productSku || skuId,
        pimSkuId: nmgPimId || pimSkuId,
      });

      // Look share via chat
      if (productInfo?.length) {
        attributes.productInfo = productInfo;
      }

      // Single Product Share via chat
      const comissionItems = pimSkuId && productId && skuId;
      if (!productInfo?.length && isComissionAttributionEnabled && isSharedProduct && comissionItems) {
        attributes.productInfo = [
          {
            pimSkuId,
            productId,
            skuId,
          },
        ];
      }
    });
  }
  if (assets?.length) {
    assets.forEach((img) => {
      attributes.attachments.push({
        url: img?.item?.url,
        resourceType: img?.item?.resourceType,
        bytes: img?.item?.bytes,
        id: img.id,
      });
    });
  }
  if (attributes?.attachments?.length && attachmentsArr?.length) {
    attributes.attachments.sort((attributeItem, prevAttributeItem) => {
      return (
        attachmentsArr.findIndex((attachmentItem) => attachmentItem.id === attributeItem.id) -
        attachmentsArr.findIndex((attachmentItem) => attachmentItem.id === prevAttributeItem.id)
      );
    });
  }
  return attributes;
};

const generateDefaultAvatar = ({ groups, brand }) => {
  const hasNMVIPGroup = (groups) => groups.some((group) => DIGITAL_STYLIST_GROUPS.indexOf(group) !== -1);
  const profileAvatar = hasNMVIPGroup(groups) ? 'NMVIP' : brand;
  return `${window.location.origin}${DEFAULT_USER_IMAGE_MAP[profileAvatar]}`;
};

const generateTrackingValue = ({ lookCategory, editCategory, isPDPView, brand, sharedLookData }) => {
  if (isPDPView) return `SP_${brand}`;

  const sharedCategoryObj = [...STYLYZE_LOOKS_LINKS, ...STYLYZE_EDIT_CATEGORIES].find(
    ({ key }) => key === (lookCategory || editCategory),
  );

  const title = sharedCategoryObj?.title.replace(' ', '_').replace(/'/g, '');

  if (lookCategory) return `Look_${title}_${brand}`;

  if (editCategory) {
    const myEditsStatus = sharedLookData?.editsStatus;
    const myEditsCategory = sharedLookData?.frame || '';

    const isMyEditsCategory = sharedCategoryObj?.key === 'MY_EDITS';
    const isHolidayCategory = ['GIFT_1', 'GIFT_4', 'GIFT_9'].includes(myEditsCategory);
    const trackingValue = ['Edit', title, brand, myEditsStatus];

    const hasKey = Object.keys(EDITS_CATEGORY).includes(myEditsCategory);

    if ((isMyEditsCategory || isHolidayCategory) && hasKey) {
      trackingValue.splice(2, 0, EDITS_CATEGORY[myEditsCategory]);
    }

    return trackingValue.filter((dataValue) => !!dataValue).join('_');
  }

  return 'no_shared_category_title_available';
};
const isAvailableToContact = (channel, client) => {
  if (channel === 'sms') {
    return !!(client.phoneNumber || client.phones?.[0]?.number);
  }

  if (channel === 'chat') {
    return !!client.ucaStanleyProfiles?.length;
  }
};

export {
  isAvailableToContact,
  alphabet,
  capitalizeString,
  formatCommaSeparatedNumber,
  groupClients,
  searchClient,
  formatEmail,
  formatPhone,
  phoneNumberFormatter,
  getProductsTotalPrice,
  getDefaultValue,
  formatImageUrl,
  formatBaseUrl,
  getNameAbbreviation,
  phoneToNumber,
  getDefaultPayment,
  getSelectedPayment,
  mapProductData,
  getSelectedValue,
  isUrlValid,
  isNumber,
  findProductInCartById,
  validateEmail,
  getRetailPrice,
  removeNumbersFromString,
  replaceNewLines,
  joinArrayOfStringsWithNewLines,
  isValidSKU,
  isValidEmail,
  getNotificationsCounter,
  getLast4Digits,
  uuidv4,
  getDefaultShipping,
  getPaymentById,
  generatePrefixedStaticIDs,
  findObjByFieldValue,
  getProductPrice,
  stringToArray,
  getInitials,
  capitalize,
  mapClientContacts,
  getAttributionQuery,
  getAppVersion,
  camelCaseToSpaceDelimited,
  getQueryParams,
  createQueryString,
  isHTML,
  getFullUserName,
  getConciergeBagTotalDetails,
  getLocaleSpecificNumericString,
  onMediaFallback,
  getNextPageCursor,
  trimWhitespaceLength,
  getPriceString,
  toTitleCase,
  _mergeArrays,
  formatKeypadPhone,
  callDuration,
  getChatMessageAttributes,
  getCompressedImageUrl,
  trimWebProductId,
  generateDefaultAvatar,
  validateName,
  validateEmailV2,
  generateTrackingValue,
};
